import React, { useCallback, useState } from 'react';
import DSSchoolSelector from '@ajat/design-system/SchoolSelector';
import useSchools from '../../../../hooks/useSchools';
import { useCart } from '../../../../context/CartContext';
import defaultBanner from '../../../../assets/cls-se/school-selector-banner.jpg';
import { stampSchoolSelection, readFreshSelection, notifySchoolSelectionUpdated } from '../../../../services/schoolSelectionTtl';

const STORAGE_KEY = 'clsse_school_selection';

/**
 * Format a date string (YYYY-MM-DD) to Danish display: "D.M.YYYY"
 */
function formatDateDK(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00'); // avoid timezone shift
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
}

function loadSelection() {
  // Honour the 24h TTL (clears expired/legacy entries).
  return readFreshSelection(STORAGE_KEY);
}

function saveSelection(school, season, cls) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stampSchoolSelection({
      school: {
        id: school.id,
        slug: school.slug,
        name: school.name,
        classes: school.classes,
        seasons: school.seasons,
      },
      season: season ? { id: season.id, slug: season.slug, name: season.name } : null,
      class: cls,
    })));
    notifySchoolSelectionUpdated(STORAGE_KEY);
  } catch { /* quota exceeded — ignore */ }
}

const SchoolSelector = ({ isOpen, onClose, onSelect, onNotFoundClick, banner = defaultBanner, ...props }) => {
  const { schools, loading } = useSchools('clsse');
  const { setSchool: setCartSchool } = useCart('clsse');

  const [saved, setSaved] = useState(loadSelection);

  const getSchoolInfo = useCallback((school) => {
    if (!school) return null;
    const data = schools.find((s) => s.id === school.id);
    if (!data?.shopOpensAt || !data?.shopClosesAt) return null;

    const now = new Date();
    const opens = new Date(data.shopOpensAt + 'T00:00:00');
    const closes = new Date(data.shopClosesAt + 'T23:59:59');
    const isOpen = now >= opens && now <= closes;

    return {
      statusLabel: isOpen
        ? 'DIN SKOLAS WEBBSHOP ÄR ÖPPEN'
        : 'DIN SKOLAS WEBBSHOP ÄR STÄNGD',
      dateRange: `${formatDateDK(data.shopOpensAt)} - ${formatDateDK(data.shopClosesAt)}`,
      description: isOpen
        ? 'Glöm inte att köpa din studentmössa under denna period för att få rabatt.'
        : null,
    };
  }, [schools]);

  const handleSelect = useCallback((selection) => {
    saveSelection(selection.school, selection.season, selection.class);
    setSaved({ school: selection.school, season: selection.season, class: selection.class });
    // Push the selection to the BE cart so orders carry school, shop and
    // class. The BE keeps the cart only when all three match its current
    // values; any change rotates to a fresh cart.
    if (selection?.school?.id) {
      setCartSchool({
        school_id: selection.school.id,
        shop_id: selection.season?.id ? Number(selection.season.id) : null,
        school_class: selection.class ?? null,
      }).catch(() => { /* cart sync is best-effort */ });
    }
    onSelect?.(selection);
  }, [onSelect, setCartSchool]);

  const handleClear = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    notifySchoolSelectionUpdated(STORAGE_KEY);
    setSaved(null);
    onSelect?.(null);
  }, [onSelect]);

  return (
    <DSSchoolSelector
      isOpen={isOpen}
      onClose={onClose}
      onSelect={handleSelect}
      onNotFoundClick={onNotFoundClick}
      banner={require('../../../../assets/cls-se/common/school-selector-banner.jpg')}
      // Only schools that currently have an open shop are selectable in the
      // popup (the API's `seasons` are the open+active shops).
      schools={loading ? [] : schools.filter((s) => (s.seasons ?? []).length > 0)}
      getSchoolInfo={getSchoolInfo}
      onClear={handleClear}
      clearLabel="Ta bort skolval"
      initialSchool={saved?.school ?? null}
      initialSeason={saved?.season ?? null}
      initialClass={saved?.class ?? null}
      requireClass={true}
      requireSeason={true}
      title="Välj din skola"
      infoText="Välj din skola för att få tillgång till unika priser, produkter och leverans till skolan."
      schoolPlaceholder="Skriv din skola här"
      seasonPlaceholder="Välj säsong"
      classPlaceholder="Välj din klass"
      submitLabel="Fortsätt"
      notFoundTitle="Hittar du inte din skola?"
      {...props}
    />
  );
};

export default SchoolSelector;
